import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const RANGE_OPTIONS = [
  { key: '7', label: '7D' },
  { key: '30', label: '30D' },
  { key: '90', label: '90D' },
  { key: '180', label: '6M' },
  { key: '365', label: '1Y' },
  { key: 'all', label: 'All' },
];

function NetWorthChart({ refreshKey }) {
  const [snapshots, setSnapshots] = useState({});
  const [range, setRange] = useState('90');
  const [loading, setLoading] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    fetchData();
  }, [refreshKey, range]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    setLoading(true);
    try {
      const end = new Date().toISOString().slice(0, 10);
      let start;
      if (range === 'all') {
        start = '2020-01-01';
      } else {
        const d = new Date();
        d.setDate(d.getDate() - parseInt(range));
        start = d.toISOString().slice(0, 10);
      }
      const res = await apiFetch(`/api/account-snapshots/range?start=${start}&end=${end}`);
      if (res.ok) setSnapshots(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const chartData = useMemo(() => {
    const dates = Object.keys(snapshots).sort();
    return dates.map((date) => {
      const accounts = snapshots[date];
      let total = 0;
      const byCategory = {};
      for (const acct of accounts) {
        const bal = parseFloat(acct.balance) || 0;
        total += bal;
        const cat = acct.category || 'Other';
        byCategory[cat] = (byCategory[cat] || 0) + bal;
      }
      return { date, total, byCategory };
    });
  }, [snapshots]);

  if (loading) {
    return (
      <section className="card">
        <h2>Net Worth Trend</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  if (chartData.length === 0) {
    return (
      <section className="card">
        <h2>Net Worth Trend</h2>
        <p style={{ color: '#71717a', fontSize: 14 }}>No snapshot data yet. Snapshots are taken daily at 6 AM.</p>
      </section>
    );
  }

  const values = chartData.map((d) => d.total);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const valRange = maxVal - minVal || 1;

  const width = 600;
  const height = 200;
  const padL = 70;
  const padR = 20;
  const padT = 20;
  const padB = 30;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;

  function x(i) { return padL + (i / Math.max(chartData.length - 1, 1)) * chartW; }
  function y(val) { return padT + chartH - ((val - minVal) / valRange) * chartH; }

  const linePath = chartData.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.total).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${x(chartData.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)} L${padL},${(padT + chartH).toFixed(1)} Z`;

  const latest = chartData[chartData.length - 1];
  const first = chartData[0];
  const change = latest.total - first.total;
  const changePct = first.total !== 0 ? (change / Math.abs(first.total)) * 100 : 0;
  const isUp = change >= 0;

  // Y-axis labels (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const val = minVal + (valRange * i) / 4;
    return { val, y: y(val) };
  });

  // X-axis labels (show ~5 dates)
  const xStep = Math.max(1, Math.floor(chartData.length / 5));
  const xTicks = chartData.filter((_, i) => i % xStep === 0 || i === chartData.length - 1);

  function formatCurrency(val) {
    if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
    if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(2)}K`;
    return `$${val.toFixed(2)}`;
  }

  function formatDate(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
  }

  return (
    <section className="card nw-chart-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Net Worth Trend</h2>
        <div className="nw-range-tabs">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              className={`nw-range-btn${range === opt.key ? ' active' : ''}`}
              onClick={() => setRange(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="nw-summary-row">
        <span className="nw-current">{formatCurrency(latest.total)}</span>
        <span className={`nw-change ${isUp ? 'positive' : 'negative'}`}>
          {isUp ? '+' : ''}{formatCurrency(change)} ({isUp ? '+' : ''}{changePct.toFixed(1)}%)
        </span>
      </div>

      <div className="nw-chart-wrapper">
        <svg viewBox={`0 0 ${width} ${height}`} className="nw-chart-svg">
          {/* Grid lines */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line x1={padL} y1={tick.y} x2={width - padR} y2={tick.y} stroke="#2a2b35" strokeWidth="1" />
              <text x={padL - 8} y={tick.y + 4} textAnchor="end" fill="#71717a" fontSize="10">
                {formatCurrency(tick.val)}
              </text>
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill={isUp ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'} />

          {/* Line */}
          <path d={linePath} fill="none" stroke={isUp ? '#22c55e' : '#ef4444'} strokeWidth="2" />

          {/* X-axis labels */}
          {xTicks.map((d) => {
            const idx = chartData.indexOf(d);
            return (
              <text key={d.date} x={x(idx)} y={height - 5} textAnchor="middle" fill="#71717a" fontSize="10">
                {formatDate(d.date)}
              </text>
            );
          })}

          {/* Hover targets */}
          {chartData.map((d, i) => (
            <circle
              key={d.date}
              cx={x(i)}
              cy={y(d.total)}
              r={hoveredPoint === i ? 4 : chartData.length > 60 ? 0 : 2}
              fill={isUp ? '#22c55e' : '#ef4444'}
              stroke="#0f1117"
              strokeWidth="1"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredPoint(i)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          ))}

          {/* Tooltip */}
          {hoveredPoint !== null && chartData[hoveredPoint] && (() => {
            const d = chartData[hoveredPoint];
            const tx = x(hoveredPoint);
            const ty = y(d.total);
            const tooltipX = tx > width / 2 ? tx - 120 : tx + 10;
            return (
              <g>
                <line x1={tx} y1={padT} x2={tx} y2={padT + chartH} stroke="#71717a" strokeWidth="1" strokeDasharray="3,3" />
                <rect x={tooltipX} y={ty - 35} width={110} height={30} rx={4} fill="#1a1b23" stroke="#3a3b45" />
                <text x={tooltipX + 8} y={ty - 21} fill="#e4e4e7" fontSize="11" fontWeight="600">
                  {formatCurrency(d.total)}
                </text>
                <text x={tooltipX + 8} y={ty - 10} fill="#71717a" fontSize="9">
                  {formatDate(d.date)}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Category breakdown for latest snapshot */}
      {latest.byCategory && (
        <div className="nw-breakdown">
          {Object.entries(latest.byCategory)
            .sort((a, b) => b[1] - a[1])
            .map(([cat, val]) => (
              <div key={cat} className="nw-breakdown-item">
                <span className="nw-breakdown-label">{cat}</span>
                <span className={`nw-breakdown-value ${val >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(val)}
                </span>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

export default NetWorthChart;
